// Contrail V3.10.0 — Admin → Activity → User activity (V3.10.1; was Admin → Online): which client users are using the platform right now, plus usage history and stats.
// Skyward admins with Support view permission only (the server checks again). Data: /api/admin/online (presence + sessions
// from migration 0020). "Now" refreshes every 15 s while the tab is visible.
import { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi, ROLE_LABEL, type Org, type Role } from "../../lib/admin";
import { Notice, fmtDateTime } from "./ui";

type State = "online" | "away" | "offline";
type LiveRow = { user_id: string; name: string; email: string | null; role: string; org_id: string | null; org_name: string; staff: boolean; state: State;
  page: string | null; app: "desktop" | "phone"; last_seen: string | null; last_active: string | null; since: string | null; today_min: number; today_sessions: number };
type Live = { at: string; counts: { online: number; away: number; companies: number; phone: number; today: number }; people: LiveRow[] };
type DayStat = { day: string; users: number; sessions: number; active_min: number; peak: number; peak_at: string | null; phone_users: number };
type CompanyStat = { org_id: string | null; org_name: string; users: number; members: number; sessions: number; active_min: number; phone_users: number; last_seen: string | null };
type UserStat = { user_id: string; name: string; email: string | null; org_name: string; role: string; staff: boolean; sessions: number; active_min: number;
  days: number; apps: ("desktop" | "phone")[]; last_seen: string | null };
type Stats = { from: string; to: string; days: DayStat[]; companies: CompanyStat[]; users: UserStat[]; truncated: boolean;
  totals: { users: number; avg_daily_users: number; peak: number; peak_at: string | null; active_hours: number; sessions: number; avg_session_min: number; phone_users: number } };

const REFRESH_MS = 15_000;
const STATE_LABEL: Record<State, string> = { online: "Online", away: "Away", offline: "Offline" };
const PAGE_LABEL: Record<string, string> = { map: "Map", m: "Phone app", reports: "Reports", fleet: "Fleet", company: "Company", admin: "Admin", s: "Share link" };
const roleLabel = (r: string) => ROLE_LABEL[r as Role] ?? r;
const mins = (m: number) => (m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} min`);
const ago = (iso: string | null | undefined, now: number) => {
  if (!iso) return "—";
  const m = (now - Date.parse(iso)) / 60000;
  return m < 1 ? "just now" : m < 90 ? `${Math.round(m)} min ago` : m < 48 * 60 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} days ago`;
};
const fijiTime = (iso: string | null | undefined) => iso
  ? new Intl.DateTimeFormat("en-FJ", { timeZone: "Pacific/Fiji", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(new Date(iso)) : "—";
const dayLabel = (d: string, long = false) => new Intl.DateTimeFormat("en-FJ", { timeZone: "UTC", day: "numeric", month: "short", ...(long ? { weekday: "short" } : {}) })
  .format(new Date(d + "T00:00:00Z"));

/** Admin → Activity → User activity (V3.10.1: was its own "Online" tab). */
export default function UserActivity({ orgs }: { orgs: Org[] }) {
  const [tab, setTab] = useState<"now" | "stats">("now");
  const [staff, setStaff] = useState(false);
  return (
    <div className="online">
      <div className="online-bar">
        <div className="online-tabs" role="tablist" aria-label="User activity">
          <button role="tab" aria-selected={tab === "now"} className={tab === "now" ? "on" : ""} onClick={() => setTab("now")}>Online now</button>
          <button role="tab" aria-selected={tab === "stats"} className={tab === "stats" ? "on" : ""} onClick={() => setTab("stats")}>History &amp; stats</button>
        </div>
        <label className="online-check"><input type="checkbox" checked={staff} onChange={(e) => setStaff(e.target.checked)} /> Include Skyward staff</label>
      </div>
      {tab === "now" ? <NowView staff={staff} orgs={orgs} /> : <StatsView staff={staff} orgs={orgs} />}
    </div>
  );
}

// ---------------------------------------------------------------- Now
function NowView({ staff, orgs }: { staff: boolean; orgs: Org[] }) {
  const [live, setLive] = useState<Live | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [org, setOrg] = useState("");
  const [showOffline, setShowOffline] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    try {
      const d = await adminApi<Live>("GET", `online${staff ? "?staff=1" : ""}`);
      if (!Array.isArray(d?.people) || !d.counts) throw new Error("Unexpected answer from the server");
      setLive(d); setErr(null);
    }
    catch (e: any) { setErr(e.message); }
    setNow(Date.now());
  }, [staff]);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === "visible") load(); }, REFRESH_MS);
    const vis = () => document.visibilityState === "visible" && load();
    document.addEventListener("visibilitychange", vis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", vis); };
  }, [load]);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (live?.people ?? []).filter((p) => (showOffline || p.state !== "offline") && (!org || p.org_id === org)
      && (!s || p.name.toLowerCase().includes(s) || (p.email ?? "").toLowerCase().includes(s) || p.org_name.toLowerCase().includes(s)));
  }, [live, q, org, showOffline]);
  const offlineCount = (live?.people ?? []).filter((p) => p.state === "offline").length;
  const companyOpts = useMemo(() => orgs.filter((o) => staff || o.type !== "internal"), [orgs, staff]);

  return (
    <>
      {err && <Notice kind="bad" onClose={() => setErr(null)}>{err}</Notice>}
      <div className="logs-tiles online-tiles">
        <div className="tile ok"><b data-k="online">{live?.counts.online ?? "–"}</b><span>Online now</span></div>
        <div className="tile warn"><b data-k="away">{live?.counts.away ?? "–"}</b><span>Away (page open, idle)</span></div>
        <div className="tile"><b data-k="companies">{live?.counts.companies ?? "–"}</b><span>Companies with someone on</span></div>
        <div className="tile"><b data-k="today">{live?.counts.today ?? "–"}</b><span>Used it today{live ? ` · ${live.counts.phone} on phones now` : ""}</span></div>
      </div>
      <div className="online-filters">
        <input className="asearch" placeholder="Search name, email or company" value={q} onChange={(e) => setQ(e.target.value)} />
        <select value={org} onChange={(e) => setOrg(e.target.value)} aria-label="Company">
          <option value="">All companies</option>
          {companyOpts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <label className="online-check"><input type="checkbox" checked={showOffline} onChange={(e) => setShowOffline(e.target.checked)} /> Also show offline in the last 24 h ({offlineCount})</label>
        <span className="sub online-updated">{live ? `Updated ${fijiTime(live.at)} · every 15 s` : "Loading…"}</span>
      </div>
      <div className="atable-wrap">
        <table className="atable online-table">
          <thead><tr><th>Person</th><th>Company</th><th>Status</th><th>Where</th><th>On since</th><th>Last active</th><th className="num">Today</th></tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.user_id} className={p.state}>
                <td><b>{p.name}</b>{p.staff && <span className="apill info" style={{ marginLeft: 6 }}>Skyward</span>}<div className="sub">{p.email}{p.role ? ` · ${roleLabel(p.role)}` : ""}</div></td>
                <td>{p.org_name}</td>
                <td><span className="online-state"><span className={`pres-dot ${p.state}`} aria-hidden="true" />{STATE_LABEL[p.state]}</span></td>
                <td>{p.state === "offline" ? <span className="sub">—</span> : <>{PAGE_LABEL[p.page ?? ""] ?? p.page ?? "—"}{p.app === "phone" && p.page !== "m" && <span className="sub"> · phone</span>}</>}</td>
                <td className="mono">{p.since ? <>{ago(p.since, now).replace(" ago", "")}<div className="sub">{fmtDateTime(p.since)}</div></> : "—"}</td>
                <td className="mono">{ago(p.last_active ?? p.last_seen, now)}</td>
                <td className="num mono">{p.today_min ? mins(p.today_min) : "—"}<div className="sub">{p.today_sessions ? `${p.today_sessions} session${p.today_sessions === 1 ? "" : "s"}` : ""}</div></td>
              </tr>
            ))}
            {live && !rows.length && <tr><td colSpan={7} className="sub">{(live.people.length ? "Nobody matches." : "Nobody has used the platform in the last 24 hours.")}</td></tr>}
            {!live && !err && <tr><td colSpan={7} className="sub">Loading…</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="sub online-note">Online = used the page in the last 2½ minutes. Away = a page is open but idle. People who close the page or sign out go offline straight away.</p>
    </>
  );
}

// ---------------------------------------------------------------- History & stats
function StatsView({ staff, orgs }: { staff: boolean; orgs: Org[] }) {
  const [days, setDays] = useState(30);
  const [org, setOrg] = useState("");
  const [stats, setStats] = useState<Stats | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [userQ, setUserQ] = useState("");

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    const qs = new URLSearchParams({ days: String(days) });
    if (staff) qs.set("staff", "1");
    if (org) qs.set("org", org);
    try {
      const d = await adminApi<Stats>("GET", `online/stats?${qs}`);
      if (!Array.isArray(d?.days) || !d.days.length || !d.totals) throw new Error("Unexpected answer from the server");
      setStats(d);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }, [days, org, staff]);
  useEffect(() => { load(); }, [load]);
  const companyOpts = useMemo(() => orgs.filter((o) => staff || o.type !== "internal"), [orgs, staff]);
  const users = useMemo(() => {
    const s = userQ.trim().toLowerCase();
    return (stats?.users ?? []).filter((u) => !s || u.name.toLowerCase().includes(s) || (u.email ?? "").toLowerCase().includes(s) || u.org_name.toLowerCase().includes(s));
  }, [stats, userQ]);
  const t = stats?.totals;

  function exportCsv() {
    if (!stats) return;
    const esc = (v: unknown) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [
      ["Day (Fiji)", "Active users", "Peak at once", "Sessions", "Active minutes", "Phone users"].join(","),
      ...stats.days.map((d) => [d.day, d.users, d.peak, d.sessions, d.active_min, d.phone_users].join(",")),
      "",
      ["Person", "Email", "Company", "Role", "Days used", "Sessions", "Active minutes", "Apps", "Last seen (Fiji)"].join(","),
      ...stats.users.map((u) => [u.name, u.email, u.org_name, roleLabel(u.role), u.days, u.sessions, u.active_min, u.apps.join(" + "), fmtDateTime(u.last_seen)].map(esc).join(",")),
    ];
    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `Contrail_Usage_${days}-days_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  }

  return (
    <>
      <div className="online-filters">
        <div className="online-tabs small" role="group" aria-label="Period">
          {[7, 30, 90, 180].map((n) => <button key={n} className={days === n ? "on" : ""} onClick={() => setDays(n)}>{n} days</button>)}
        </div>
        <select value={org} onChange={(e) => setOrg(e.target.value)} aria-label="Company">
          <option value="">All companies</option>
          {companyOpts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <span className="sub online-updated">{stats ? `${dayLabel(stats.days[0].day)} – ${dayLabel(stats.days[stats.days.length - 1].day)} (Fiji days)` : ""}</span>
        <button className="btn-ghost" onClick={exportCsv} disabled={!stats}>Export CSV</button>
        <button className="btn-primary" onClick={load} disabled={busy}>{busy ? "Loading…" : "Refresh"}</button>
      </div>
      {err && <Notice kind="bad" onClose={() => setErr(null)}>{err}</Notice>}
      {stats?.truncated && <Notice kind="warn">Very busy period: only the first 50,000 sessions were counted. Choose a shorter period.</Notice>}

      <div className="logs-tiles online-tiles six">
        <div className="tile"><b data-k="users">{t?.users ?? "–"}</b><span>People who used it</span></div>
        <div className="tile"><b data-k="avg">{t?.avg_daily_users ?? "–"}</b><span>Average per day</span></div>
        <div className="tile"><b data-k="peak">{t?.peak ?? "–"}</b><span>Most at once{t?.peak_at ? ` · ${fmtDateTime(t.peak_at)}` : ""}</span></div>
        <div className="tile"><b data-k="hours">{t?.active_hours ?? "–"}</b><span>Active hours</span></div>
        <div className="tile"><b data-k="sessions">{t?.sessions ?? "–"}</b><span>Sessions · avg {t ? mins(t.avg_session_min) : "–"}</span></div>
        <div className="tile"><b data-k="phone">{t?.phone_users ?? "–"}</b><span>Used the phone app</span></div>
      </div>

      {stats && <DailyChart days={stats.days} />}

      <h2 className="online-h2">By company</h2>
      <div className="atable-wrap">
        <table className="atable online-companies">
          <thead><tr><th>Company</th><th className="num">People active</th><th className="num">Sessions</th><th className="num">Active time</th><th className="num">On phones</th><th>Last seen</th></tr></thead>
          <tbody>
            {(stats?.companies ?? []).map((c) => (
              <tr key={c.org_id ?? "none"}>
                <td><b>{c.org_name}</b></td>
                <td className="num mono">{c.users}{c.members ? <span className="sub"> of {c.members}</span> : null}</td>
                <td className="num mono">{c.sessions}</td>
                <td className="num mono">{mins(c.active_min)}</td>
                <td className="num mono">{c.phone_users}</td>
                <td className="mono">{fmtDateTime(c.last_seen)}</td>
              </tr>
            ))}
            {stats && !stats.companies.length && <tr><td colSpan={6} className="sub">No use recorded in this period yet. History starts from V3.10.0.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="online-h2row">
        <h2 className="online-h2">People</h2>
        <input className="asearch" placeholder="Search people" value={userQ} onChange={(e) => setUserQ(e.target.value)} />
      </div>
      <div className="atable-wrap">
        <table className="atable online-users">
          <thead><tr><th>Person</th><th>Company</th><th className="num">Days used</th><th className="num">Sessions</th><th className="num">Active time</th><th>App</th><th>Last seen</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id}>
                <td><b>{u.name}</b>{u.staff && <span className="apill info" style={{ marginLeft: 6 }}>Skyward</span>}<div className="sub">{u.email}{u.role ? ` · ${roleLabel(u.role)}` : ""}</div></td>
                <td>{u.org_name}</td>
                <td className="num mono">{u.days}</td>
                <td className="num mono">{u.sessions}</td>
                <td className="num mono">{mins(u.active_min)}</td>
                <td>{u.apps.map((a) => (a === "phone" ? "Phone" : "Desktop")).join(" + ")}</td>
                <td className="mono">{fmtDateTime(u.last_seen)}</td>
              </tr>
            ))}
            {stats && !users.length && <tr><td colSpan={7} className="sub">Nobody to show.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="sub online-note">A session runs from opening a page until it's closed or quiet for 5 minutes. Active time only counts minutes with mouse, keyboard or touch use. History is kept for 180 days.</p>
    </>
  );
}

/** Daily active users as bars (one series, one hue); hover a bar for the day's details. */
function DailyChart({ days }: { days: DayStat[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 960, H = 200, L = 34, R = 8, T = 12, B = 26;
  const max = Math.max(1, ...days.map((d) => d.users));
  const step = max <= 5 ? 1 : max <= 10 ? 2 : max <= 25 ? 5 : max <= 50 ? 10 : Math.ceil(max / 5 / 10) * 10;
  const top = Math.ceil(max / step) * step;
  const iw = W - L - R, ih = H - T - B, bw = iw / days.length;
  const y = (v: number) => T + ih - (v / top) * ih;
  const ticks = Array.from({ length: top / step + 1 }, (_, i) => i * step);
  const every = Math.ceil(days.length / 10);
  const h = hover != null ? days[hover] : null;
  return (
    <figure className="online-chart">
      <figcaption>Active people per day</figcaption>
      <div className="online-chart-box">
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Active people per day, highest ${max}`} onMouseLeave={() => setHover(null)}>
          {ticks.map((v) => <g key={v}><line x1={L} x2={W - R} y1={y(v)} y2={y(v)} className="grid" /><text x={L - 6} y={y(v) + 4} textAnchor="end" className="tick">{v}</text></g>)}
          {days.map((d, i) => {
            const x = L + i * bw, w = Math.max(1, bw - 2), bh = Math.max(0, T + ih - y(d.users));
            const r = Math.min(4, w / 2, bh);
            return (
              <g key={d.day} onMouseEnter={() => setHover(i)}>
                <rect x={x} y={T} width={bw} height={ih} fill="transparent" />
                {d.users > 0 && <path className={`bar ${hover === i ? "on" : ""}`} d={`M${x + 1},${T + ih} v${-(bh - r)} q0,${-r} ${r},${-r} h${w - 2 * r} q${r},0 ${r},${r} v${bh - r} z`} />}
                {(i % every === 0 || i === days.length - 1) && <text x={x + bw / 2} y={H - 8} textAnchor="middle" className="tick">{dayLabel(d.day)}</text>}
              </g>
            );
          })}
        </svg>
        {h && hover != null && (
          <div className="online-tip" style={{ left: `${((L + (hover + 0.5) * bw) / W) * 100}%` }} role="status">
            <b>{dayLabel(h.day, true)}</b>
            <div>{h.users} {h.users === 1 ? "person" : "people"} · most at once {h.peak}</div>
            <div>{h.sessions} session{h.sessions === 1 ? "" : "s"} · {mins(h.active_min)} active</div>
            {h.phone_users > 0 && <div>{h.phone_users} on the phone app</div>}
          </div>
        )}
      </div>
    </figure>
  );
}
